import React from "react";
import styled from "styled-components";
import { constants } from "@thecb/components";
import { noop } from "@/util/common";
import {
  WHITE,
  MINESHAFT_GREY,
  TEXT_PRIMARY,
  AI_ACCENT,
  CITYVILLE_GREY,
} from "@/constants/colors";
import {
  FILTERS_CONTAINER_ID,
  FILTERS_TOGGLE_LABELS,
  FILTERS_VIEWS,
  filtersToggleLabel,
  isFiltersPanelVisible,
  showsEveryFilter,
} from "./filterPresentation";

// `$color-accent-light` and `$color-accent-text` from styles/_variables.scss.
// The design system ships the same two values as HAWKES_BLUE and LUCKY_POINT,
// so the toggle reads them from there rather than repeating the hex codes.
const { LUCKY_POINT, HAWKES_BLUE } = constants.colors;

// Collapsed the toggle is a plain white button. Expanded it takes the brand
// accent tint. Hover in both states matches the "Download X Users" secondary
// button on the Settings tab: accent-tinted face, accent text and border
// (WCHK-3048).
const ToggleButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 52px;
  padding: 0 16px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-family: inherit;
  line-height: 1.4;
  white-space: nowrap;
  cursor: pointer;
  background: ${({ $isActive }) => ($isActive ? HAWKES_BLUE : WHITE)};
  color: ${({ $isActive }) => ($isActive ? TEXT_PRIMARY : MINESHAFT_GREY)};
  /* Collapsed, a grey border keeps the control boundary above the 3:1 non-text
     contrast floor (WCAG 2.2 AA, 1.4.11): CITYVILLE_GREY reads 4.9:1 on white.
     Expanded, the tinted fill is the state indicator and the border goes.
     Hover brings the accent border back in both states (WCHK-3048 review). */
  border: 1px solid
    ${({ $isActive }) => ($isActive ? "transparent" : CITYVILLE_GREY)};
  &:hover {
    background: ${HAWKES_BLUE};
    color: ${LUCKY_POINT};
    border-color: ${LUCKY_POINT};
  }
  &:focus-visible,
  &:focus {
    outline: 2px solid ${AI_ACCENT};
    outline-offset: 2px;
  }
`;

// Reserved against by name: character count is no proxy for rendered width.
const WIDEST_TOGGLE_LABEL = FILTERS_TOGGLE_LABELS.SHOW_ALL;

/**
 * ToggleLabel
 *
 * Holds the label at the widest label's width, so a label change cannot take
 * width from the `flex-shrink: 0` toggle's neighbour and shift the row
 * (WCHK-3037 review). The reserving pseudo-element keeps one label in the
 * accessibility tree.
 */
const ToggleLabel = styled.span`
  display: inline-grid;
  justify-items: center;
  & > span,
  &::after {
    grid-area: 1 / 1;
    white-space: nowrap;
  }
  &::after {
    content: "${WIDEST_TOGGLE_LABEL}";
    visibility: hidden;
    pointer-events: none;
  }
`;

const FilterIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    aria-hidden="true"
  >
    <path d="M2.5 4.5H13.5" />
    <path d="M4.5 8H11.5" />
    <path d="M6.5 11.5H9.5" />
  </svg>
);

/**
 * FiltersToggle
 *
 * Show/Hide control for the filters container. Stateless on purpose: the view
 * lives in the transaction-search reducer so it persists across tab
 * navigation (WCHK-2829).
 *
 * `aria-expanded` reports whether every filter is showing, which is a separate
 * question from whether the panel is on the page (WCHK-3037).
 *
 * @param {string} view - One of FILTERS_VIEWS.
 * @param {boolean} hasPopulatedFilters - Whether any filter holds a value.
 * @param {Function} onToggle - Called on click; the page moves the view on.
 * @param {string} controlsId - id of the filters container, referenced by
 * aria-controls while it is on the page and dropped when it is unmounted.
 * @param {string} dataQa - Test/QA hook.
 */
const FiltersToggle = ({
  view = FILTERS_VIEWS.COLLAPSED,
  hasPopulatedFilters = false,
  onToggle = noop,
  controlsId = FILTERS_CONTAINER_ID,
  dataQa = "nl-filters-toggle",
}) => {
  const panelVisible = isFiltersPanelVisible(view, hasPopulatedFilters);
  const expanded = showsEveryFilter(view);

  return (
    <ToggleButton
      type="button"
      $isActive={panelVisible}
      onClick={onToggle}
      aria-expanded={expanded}
      aria-controls={panelVisible ? controlsId : undefined}
      data-active={panelVisible ? "true" : "false"}
      data-qa={dataQa}
      data-testid={dataQa}
    >
      <FilterIcon />
      <ToggleLabel>
        <span>{filtersToggleLabel(view, hasPopulatedFilters)}</span>
      </ToggleLabel>
    </ToggleButton>
  );
};

export default FiltersToggle;
